/* global JWPLAYER_LICENSE_KEY */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import ReactGA from 'react-ga4'
import logger from 'components/utils/logger'
import notifications, { toastError } from '../../global/notifications'

import { playerSelector as stateSelector } from 'reduxModules/player/selectors'
import { playerDispatchers as dispatchers } from 'reduxModules/player/dispatchers'

import JWPlayer from '@jwplayer/jwplayer-react'
import _ from 'lodash'

import {
  isApiVideoUrl,
  signedVariantUrl,
  withPrivateVideoCredentials,
  withSignedVariant,
  playlistRequiresCredentials
} from '../../utils/privateVideo'

import { TutorialSummaryPropTypes } from 'components/generic/CourseSummary/propTypes'
import RelatedModal from './RelatedModal'

import {
  TutorialPropTypes,
  TutorialDefaultProps
} from '../Tutorials/TutorialDetail'
import PlayerSidebar from './PlayerSidebar'

import {
  getChapterFromURL,
  getSecondsFromTimestamp,
  getChapterTimePositionFromURL
} from './utils'

import './styles.css'
import { COMPLETION_THRESHOLD } from './PlayerSidebar/ChapterList'

const CHAPTER_PROGRESS_UPDATE_FREQUENCY = 1000 // One second
const TICK_FREQUENCY = 5000 // Five seconds
const MEANINGFUL_CONTENT_LENGTH = 360 // Six minutes
// Consecutive recovery attempts allowed before we give up and surface an error.
export const MAX_RECOVERY_ATTEMPTS = 3
// Hard ceiling on recoveries for the whole page-load, independent of any reset.
// Backstop against a stream that plays briefly, dies, and re-arms forever.
export const ABSOLUTE_MAX_RECOVERIES = 10
// Seconds of playback past the resume point that count a recovery as succeeded.
const SUSTAINED_PLAYBACK_SECONDS = 3
// Base backoff between attempts; doubles each attempt (1s / 2s / 4s).
const RECOVERY_BACKOFF_BASE_MS = 1000
// SPEC-057: consecutive failed signed-manifest probes before giving up for
// the page load.
export const MAX_SIGNED_PROBE_FAILURES = 3
// SPEC-057 AC-1: reloads of the same chapter key in one page load beyond this
// are logged as a defect signal — no legitimate flow needs anywhere near it.
export const CHAPTER_RELOAD_ALARM_THRESHOLD = 10
// The playlist's identity can churn while its content is unchanged; only
// content differences justify handing JW a new load (and the manifest
// refetch that comes with it).
const playlistSignature = (list) =>
  (list || [])
    .map((item) =>
      (item.sources || [])
        .map((source) => `${source.file}|${source.withCredentials ? 1 : 0}`)
        .join(',')
    )
    .join(';')

function NewVideoPlayer(props) {
  /**
   * Props
   */
  const {
    chapters,
    startTutorialStatistics,
    tutorial = { ...TutorialDefaultProps },
    setCurrentChapter,
    videoId,
    bookmarkLoaded,
    resume = null,
    currentChapterIndex,
    recommends,
    played,
    disableStats = false,
    videoStatisticId = null,
    loadNextTutorials,
    sendTick,
    isSubscriptionApprentice,
    apprenticeVideosSeen,
    addVideoSeen,
    isMobile = false,
    markChapterAsCompleted,
    chaptered,
    isSpecialVideo = false,
    createBookmark,
    toggleChaptersSidebar,
    hideSidebar,
    displayCreateBookmark,
    removeBookmark,
    userBookmarks,
    savePlayerId,
    learningPath,
    shouldOnlyShowRelevants,
    toggleShowingOnlyRelevantChapters,
    relevantChapters,
    cleanPlayer,
    updateUserProgress,
    userProgress,
    slug,
    router,
    isBasicSubscriber,
    refreshPlaylist,
    // SPEC-122 FR-5: the page's own answer to a denied playlist
    onNoAccess
  } = props
  /**
   * States
   */
  const [player, setPlayer] = useState(null)
  const [displayModal, setDisplayModal] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playerIsReady, setPlayerIsReady] = useState(false)
  const [playlistIsComplete, setPlaylistIsComplete] = useState(false)
  const [recommendedWereDisplayed, setRecommendedWereDisplayed] =
    useState(false)
  // SPEC-010 FR-7: once a cast target is around, every API playlist URL gets
  // `signed=1` so the manifests come back with cookie-free, query-signed
  // segment URLs a receiver can fetch. Flipped by setupSignedVariant.
  const [signedVariant, setSignedVariant] = useState(false)
  // SPEC-054 FR-7: when the playlist rate limiter blocks the viewer, say so
  // in the player area with an accurate countdown instead of failing
  // silently — {detail, resumesAtMs} from the 429 body.
  const [rateLimit, setRateLimit] = useState(null)
  const [rateLimitMinutesLeft, setRateLimitMinutesLeft] = useState(0)
  // SPEC-010: private-host sources get withCredentials so segment/manifest
  // requests carry the CloudFront signed cookies; public hosts stay
  // uncredentialed.
  const credentialedPlaylist = useMemo(
    () =>
      withPrivateVideoCredentials(
        signedVariant ? withSignedVariant(chapters) : chapters
      ),
    [chapters, signedVariant]
  )
  // Belt-and-braces: set withCredentials at the JW setup level too, so the
  // player's OWN manifest/fragment XHRs carry cookies even if JW does not
  // propagate the per-source flag to hls.js. Gated on the sources actually
  // targeting a credentialed host so public playlists (some special videos)
  // are not credentialed against CORS that forbids it.
  const requiresCredentials = useMemo(
    () => playlistRequiresCredentials(chapters),
    [chapters]
  )
  /**
   *  Methods
   */
  // Keeps the chapter query param url in sync when present
  const updateCurrentChapterURL = (index, shouldClearTime) => {
    // Don't update URL if is a special video (no chapters)
    if (isSpecialVideo) return

    const searchParams = new URLSearchParams(window.location?.search)
    const position = index + 1

    if (index !== undefined) {
      if (
        searchParams.has('chapter') &&
        searchParams.get('chapter') !== position
      )
        searchParams.set('chapter', position)
    } else searchParams.delete('chapter')

    // Remove time query param if requested (auto-advancing case)
    if (shouldClearTime) searchParams.delete('time')
    // Push new route
    router.push(`/tutorials/${slug}?${searchParams.toString()}`)
  }
  const playChapter = (index, time) => {
    // Get current playlist item playing
    const currentlyPlaying = player.getPlaylistIndex()
    const playlist = player.getPlaylist()
    const chapterId = playlist[index]?.extra?.id
    const isChangingChapters =
      currentChapterIndex !== index || index !== currentlyPlaying
    // If no valid index is passed, play from the beginning
    if (index === -1) player.playlistItem(0)
    // If chapter to play is not already playing, jump to play it
    if (isChangingChapters) player.playlistItem(index)

    // Update current chapter
    setCurrentChapter(videoId, index, chapterId)
    updateCurrentChapterURL(index)
    // Scroll to the top if mobile
    if (isMobile) window.scrollTo(0, 0)

    if (time !== undefined)
      if (isChangingChapters)
        // If time is valid, subscribe to jump to time after auto-play's "play" event is triggered
        player.once('play', () => player.seek(time))
      // If there is no chapter change, seek immediately
      else player.seek(time)
  }

  const throttledChapterProgressUpdate = useCallback(
    _.throttle((currentTime, currentChapterId, currentDuration) => {
      updateUserProgress({
        id: currentChapterId,
        currentPosition: currentTime,
        duration: currentDuration
      })
    }, CHAPTER_PROGRESS_UPDATE_FREQUENCY),
    []
  )
  const cancelChapterProgressUpdate = () => {
    // If a progress update is pending from previous chapter, cancel it
    throttledChapterProgressUpdate.cancel()
  }
  /**
   *  Handlers
   */
  // Consecutive recovery attempts that have not yet produced sustained playback.
  const recoveryAttempts = useRef(0)
  // Recoveries over the whole page-load; never reset — the absolute backstop.
  const totalRecoveries = useRef(0)
  // Playhead position at the last recovery, or null when not recovering. The
  // `time` handler uses it to decide when playback has been sustained.
  const recoveryPosition = useRef(null)
  // Pending backoff timer, cleared on unmount.
  const recoveryTimeoutId = useRef(null)
  // Whether the signed-URL variant has been requested for cast receivers
  // (SPEC-010 FR-7 / SPEC-056): set on cast INTENT — the user reaching for
  // the cast/AirPlay button — and kept for the rest of the page load; see
  // setupSignedVariant. Mirrors the `signedVariant` state for handlers bound
  // once in onReady.
  const signedVariantActive = useRef(false)
  // Whether a cast session (Chromecast or AirPlay) is currently active. Only
  // used to surface the daily-cap toast when the user actually casts.
  const castSessionActive = useRef(false)
  // Set while a signed-variant swap is in flight so the playlist-reload effect
  // restores the current chapter/position instead of resetting to item 0.
  const restoreOnReload = useRef(false)
  // The signed variant is capped per viewer per day (backend 429). Once hit,
  // stop requesting it for the rest of the page load so neither the cast swap
  // nor a mid-cast recovery hammers the capped endpoint.
  const signedVariantBlocked = useRef(false)
  // SPEC-057: consecutive failed signed-manifest probes. The non-429 failure
  // path re-opens the swap guard so a transient error can be retried on the
  // next cast gesture — but unbounded re-entry is how loops are born, so
  // after MAX_SIGNED_PROBE_FAILURES the variant is treated as blocked for
  // the rest of the page load (the cast-start toast then tells the user).
  const signedProbeFailures = useRef(0)
  // SPEC-057 FR-4: the reload effect fires on playlist IDENTITY changes, but
  // upstream state churn can mint fresh identities for identical content —
  // and every player.load() makes JW refetch the current manifest, outside
  // any recovery budget (production: one page load fetching the same chapter
  // key 215 times while playback worked). Loads are therefore de-duplicated
  // by CONTENT signature, and per-chapter load counts are kept so a future
  // loop announces itself in Sentry instead of running silently at 22% of
  // users.
  const lastLoadSignature = useRef(null)
  const chapterLoadCounts = useRef({})
  // SPEC-054 FR-7: epoch ms until which the playlist limiter blocks us — no
  // auto-retry sooner (the window is fixed; the countdown is accurate).
  const rateLimitedUntil = useRef(0)
  // Handler for when the playlist index changes to a new playlist item. This event occurs before the player begins playing the new playlist item.
  const playlistItemHandler = ({ item }) => {
    const playlistItemId = item?.extra?.id
    const playlist = player.getPlaylist()
    const currentChapterIndex = playlist.findIndex(
      (chapter) => chapter?.extra?.id === playlistItemId
    )
    const chapterIndexToUpdate =
      currentChapterIndex === -1 ? 0 : currentChapterIndex
    // Abort any progress tracking since playlist items are changing
    cancelChapterProgressUpdate()
    // A recovery's resume point only applies to the item it was captured in;
    // once the item changes, drop it so the `time` handler's sustained-playback
    // check can't fire against a position from a different chapter.
    recoveryPosition.current = null
    // Update redux and URL with new chapter as current chapter
    setCurrentChapter(videoId, chapterIndexToUpdate, playlistItemId)
  }
  // Handler when the playlist is completed (the course finished)
  const playlistCompleteHandler = () => {
    setCurrentChapter(videoId, 0, null)
    updateCurrentChapterURL(0)
    setIsPlaying(false)
    setPlaylistIsComplete(true)
    cancelChapterProgressUpdate()
  }
  const playHandler = ({ playReason }) => {
    setIsPlaying(true)
    // NOTE: the recovery budget is intentionally NOT reset here. A bare `play`
    // event does not mean playback is sustained — the reset lives in the `time`
    // handler, gated on SUSTAINED_PLAYBACK_SECONDS of real progress.

    // Auto-advance to previous playback position if chapter is playing due to autoadvancing
    if (playReason === 'playlist') {
      const currentChapterIndex = player.getPlaylistIndex()
      const currentChapter = player.getPlaylistItem(currentChapterIndex)
      const playlistItemId = currentChapter?.extra?.id
      const progress = userProgress[playlistItemId]

      if (progress?.currentPosition > 0 && !progress?.completed)
        player.seek(progress?.currentPosition)

      // Update query param and remove time query param if present to keep it consistent in case the user reloads the page
      updateCurrentChapterURL(currentChapterIndex, true)
    }

    if (disableStats) return

    /* Mark video as seen */
    if (
      isSubscriptionApprentice &&
      _.findIndex(apprenticeVideosSeen, { id: tutorial.id }) === -1 &&
      !tutorial.owned
    )
      addVideoSeen(tutorial)
  }
  const RECOVERABLE_ERROR_CODES = [230002, 233011]
  // JW reports a manifest request the API answered 429 / 403 as 232429 /
  // 232403 (232 + the HTTP status). The body never reaches JW that way.
  const MANIFEST_RATE_LIMITED_ERROR = 232429
  const MANIFEST_FORBIDDEN_ERROR = 232403
  // SPEC-122 FR-5: the playlist endpoint now enforces course access and
  // answers a denied request with this code (no cookie); the page's own
  // locked/subscribe state is the answer, not a player error
  const PLAYLIST_NO_ACCESS = 'playlist_no_access'
  // SPEC-054 FR-7: the limiter's 429 carries an accurate window — surface it
  // and stop retrying until it ends. True when the response was such a 429.
  const surfaceRateLimit = (res) => {
    if (res?.payload?.status !== 429) return false
    const body = res.payload.value || {}
    const seconds = body.retry_after_seconds || 60
    rateLimitedUntil.current = Date.now() + seconds * 1000
    setRateLimit({
      detail:
        body.detail || 'Playback is paused by a temporary viewing limit.',
      resumesAtMs: rateLimitedUntil.current,
      // SPEC-115: a blocked account has no window to count down —
      // only a human lifts it (the detail says to contact support)
      blocked: body.code === 'playlist_blocked'
    })
    player.pause()
    return true
  }
  // JW raises this when the cast receiver cannot load the source it was
  // handed — the signature of a cookie-only manifest reaching a receiver.
  const CAST_RECEIVER_LOAD_ERROR = 350000
  // JW's `error` / `setupError` events are the error itself ({ code,
  // message, sourceError, type }); the tests have always sent { error }.
  // Read both: the destructured form made every real event look codeless.
  const errorHandler = (event = {}) => {
    const error = event.error || event
    // SPEC-056 AC-2 was waived for lack of cast hardware, and this failure is
    // otherwise visible only to the viewer (JW tears the session down; we
    // hear about it as a support ticket). Recording the swap state alongside
    // it makes a mis-firing intent hook show up in Sentry within days.
    if (error?.code === CAST_RECEIVER_LOAD_ERROR) {
      logger.error('Cast receiver failed to load the source (SPEC-056)', {
        error,
        signedVariantActive: signedVariantActive.current,
        signedVariantBlocked: signedVariantBlocked.current,
        castSessionActive: castSessionActive.current
      })
      try {
        ReactGA.event({
          category: 'Player',
          action: 'cast_receiver_error',
          label: signedVariantActive.current ? 'signed' : 'plain'
        })
      } catch (_) {
        // analytics must never mask the error
      }
      return
    }
    // The playlist (manifest) endpoint is the only one the limiter and the
    // SPEC-115 block gate, and JW fetches it itself, so its 429 body never
    // reaches us. Ask the same URL once (it carries its own token) and show
    // the notice instead of JW's generic error. Inside a known window there
    // is nothing new to learn.
    if (
      error?.code === MANIFEST_RATE_LIMITED_ERROR ||
      error?.code === MANIFEST_FORBIDDEN_ERROR
    ) {
      if (Date.now() < rateLimitedUntil.current) return
      const file = player.getPlaylistItem?.()?.file
      if (!file) {
        logger.error(error)
        return
      }
      fetch(file, { credentials: 'include' })
        .then((res) => {
          if (res.status !== 429 && res.status !== 403) return false
          return res
            .json()
            .catch(() => ({}))
            .then((value) => {
              if (res.status === 429)
                return surfaceRateLimit({ payload: { status: 429, value } })
              if (value?.code !== PLAYLIST_NO_ACCESS) return false
              player.stop()
              if (onNoAccess) onNoAccess(value)
              else toastError(notifications.forbiddenToWatch)
              return true
            })
        })
        .then((handled) => {
          if (!handled) logger.error(error)
        })
        .catch((err) => {
          logger.error('Manifest lookup failed', { error: err })
        })
      return
    }
    const canRecover =
      RECOVERABLE_ERROR_CODES.includes(error?.code) && refreshPlaylist
    if (!canRecover) {
      logger.error(error)
      return
    }

    // Give up once either budget is spent: the consecutive-attempt budget (this
    // recovery burst is not working) or the absolute per-page-load ceiling.
    // Surface it so the user knows to reload instead of watching an endless
    // spinner
    if (
      recoveryAttempts.current >= MAX_RECOVERY_ATTEMPTS ||
      totalRecoveries.current >= ABSOLUTE_MAX_RECOVERIES
    ) {
      logger.error('Stream recovery budget exhausted, giving up', { error })
      toastError(notifications.playbackRecoveryFailed)
      return
    }

    // SPEC-054 FR-7: inside a rate-limit window every retry is a guaranteed
    // 429 — don't burn the budget or hammer the endpoint; the countdown
    // effect retries once when the window ends.
    if (Date.now() < rateLimitedUntil.current) return

    recoveryAttempts.current++
    totalRecoveries.current++
    const attempt = recoveryAttempts.current
    const currentPosition = player.getPosition()
    const currentChapterIndex = player.getPlaylistIndex()
    // Mark where we resumed from; the `time` handler clears the budget only once
    // playback advances SUSTAINED_PLAYBACK_SECONDS past this point.
    recoveryPosition.current = currentPosition

    logger.error(
      `Stream expired, attempting recovery (${attempt}/${MAX_RECOVERY_ATTEMPTS})`
    )

    // Back off between attempts (1s / 2s / 4s) rather than hammering the origin.
    const backoffMs = RECOVERY_BACKOFF_BASE_MS * 2 ** (attempt - 1)
    // Cancel any pending recovery before arming a new one so overlapping error
    // events can't leave two recovery callbacks racing to refresh and seek.
    if (recoveryTimeoutId.current) clearTimeout(recoveryTimeoutId.current)
    recoveryTimeoutId.current = setTimeout(() => {
      // This timer has fired; clear the handle so it reflects no pending work.
      recoveryTimeoutId.current = null
      // The refreshed sources are re-flagged with signed=1 by the playlist
      // memo while the signed variant is active, so a recovery never strands
      // a cast receiver on a cookie-only manifest. The flag is also passed
      // along for the endpoint's benefit; it is not what selects the variant.
      refreshPlaylist({
        signed: signedVariantActive.current && !signedVariantBlocked.current
      })
        .then(([, res] = []) => {
          if (surfaceRateLimit(res)) return
          player.playlistItem(currentChapterIndex)
          player.seek(currentPosition)
          // Intentionally NO reset here — a resolved fetch is not proof of
          // playback. Recovery counts as succeeded only after sustained
          // playback, handled in the `time` handler.
        })
        .catch((err) => {
          logger.error('Recovery failed', { error: err })
        })
    }, backoffMs)
  }
  // SPEC-010 FR-7 / SPEC-056: cast receivers (Chromecast/AirPlay) fetch the
  // stream themselves and never carry the browser's cookies, so a
  // cookie-only manifest would 403 on the receiver — and JW hands the
  // receiver the current source the moment the session connects (a swap
  // after the fact can never land in time: first receiver load error raises
  // 350000 and tears the session down). But swapping on mere AVAILABILITY
  // handed harvestable signed URLs to ~25% of playback and burned the cast
  // cap for people who never cast (production APM, 2026-09-18). The trigger
  // is therefore cast INTENT — the user pressing the cast/AirPlay button —
  // which precedes session connection by the whole device-picker + receiver
  // launch window: Chromecast via a requestSession wrap + the SDK's
  // CONNECTING state, AirPlay via the picker call itself (see the hooks
  // below). Availability only attaches those hooks. The signed variant also
  // plays in the browser, so it is kept for the rest of the page load rather
  // than reverted when the session ends (a revert would cost another capped
  // signed request the next time the user casts).
  const setupSignedVariant = (trigger = 'cast') => {
    if (signedVariantActive.current || signedVariantBlocked.current) return
    const current = player.getPlaylist()?.[player.getPlaylistIndex()]
    const source = current?.sources?.find((s) => isApiVideoUrl(s.file))?.file
    if (!source) return
    signedVariantActive.current = true
    // NFR-1: the signed variant must stay rare — leave a queryable signal of
    // every activation and what pulled it, so the ratio is checkable without
    // spelunking APM
    try {
      ReactGA.event({
        category: 'Player',
        action: 'signed_variant_activated',
        label: trigger
      })
    } catch (_) {
      // analytics must never block the swap
    }
    // Probe the signed master manifest before touching playback: the variant
    // is capped per viewer per day (backend 429) and, once tripped, every
    // signed request fails — a blind swap would drop the browser into the
    // recovery loop. The cap counts distinct chapters, so this fetch is the
    // same request JW is about to make and costs nothing extra.
    fetch(signedVariantUrl(source), { credentials: 'include' })
      .then((res) => {
        if (res.status === 429) {
          // Browser playback is unaffected, so only tell the user once they
          // actually cast.
          signedVariantBlocked.current = true
          signedVariantActive.current = false
          if (castSessionActive.current) toastError(notifications.castingLimit)
          return
        }
        if (!res.ok)
          throw new Error(`Signed manifest probe: HTTP ${res.status}`)
        // The reload effect captures chapter/position right before reloading
        // and restores them, so a swap landing mid-resume (or while paused)
        // doesn't disturb playback.
        restoreOnReload.current = true
        setSignedVariant(true)
      })
      .catch((err) => {
        signedVariantActive.current = false
        signedProbeFailures.current += 1
        // SPEC-057: bounded re-entry — a transient probe failure may retry on
        // the next cast gesture, but after the cap the variant stays off for
        // this page load (the cast-start toast informs the user)
        if (signedProbeFailures.current >= MAX_SIGNED_PROBE_FAILURES)
          signedVariantBlocked.current = true
        logger.error('Cast playlist variant swap failed', { error: err })
      })
  }
  const setCastSession = (active, trigger = 'cast-session-active') => {
    if (castSessionActive.current === active) return
    castSessionActive.current = active
    if (!active) return
    // A session started without a prior availability event (or the swap was
    // capped): the receiver is about to fail on the cookie-only manifest.
    if (signedVariantBlocked.current) toastError(notifications.castingLimit)
    else setupSignedVariant(trigger)
  }
  // SPEC-056: Chromecast intent hooks. JW's cast button calls the Cast SDK's
  // CastContext.requestSession(), and the receiver is only handed a source
  // AFTER the session resolves (device picked + receiver app launched) — so
  // wrapping requestSession fires the swap at the earliest possible moment,
  // with the whole picker-and-launch window as margin. The SDK's CONNECTING
  // state (device picked) is the belt-and-braces for session joins that skip
  // requestSession (e.g. auto-join). Attached lazily on the first
  // availability event, when the SDK is guaranteed loaded; Safari has no
  // window.cast, so this no-ops there.
  const chromecastHookAttached = useRef(false)
  const attachChromecastIntentHook = () => {
    if (chromecastHookAttached.current) return
    try {
      const framework = window.cast?.framework
      if (!framework) return
      const context = framework.CastContext.getInstance()
      const original = context.requestSession.bind(context)
      context.requestSession = () => {
        setupSignedVariant('chromecast-intent')
        return original()
      }
      context.addEventListener(
        framework.CastContextEventType.CAST_STATE_CHANGED,
        (event) => {
          if (event.castState === framework.CastState.CONNECTING)
            setupSignedVariant('chromecast-connecting')
        }
      )
      // A session that predates this hook (SDK auto-rejoin, or a session
      // surviving an in-app navigation) never fires CONNECTING again — the
      // state is already CONNECTING/CONNECTED when we attach, so check it
      // directly. Post-connect the swap may not land before the receiver's
      // first fetch, but the reload re-hands it a signed source.
      const state = context.getCastState?.()
      if (
        state === framework.CastState.CONNECTING ||
        state === framework.CastState.CONNECTED
      )
        setupSignedVariant('chromecast-resumed')
      chromecastHookAttached.current = true
    } catch (err) {
      // SDK surface changed or unavailable: the session-active fallback in
      // setCastSession still attempts the swap (it may not land in time,
      // but it is strictly better than nothing)
      logger.error('Chromecast intent hook failed', { error: err })
    }
  }
  // JW's `cast` event carries { available, active } for both Chromecast
  // (Chrome) and AirPlay (Safari, via its airplay controller). Availability
  // no longer swaps anything — a Chromecast on the LAN is not intent
  // (SPEC-056: that leaked signed URLs to ~25% of playback) — it only
  // attaches the intent hooks now that the SDK is up.
  const castHandler = ({ available, active }) => {
    if (available) attachChromecastIntentHook()
    setCastSession(Boolean(active))
  }
  // Safari AirPlay: the JW airplay button opens the system device picker via
  // the media element's webkitShowPlaybackTargetPicker() — that call IS the
  // user's intent, and the receiver only gets the stream after the user
  // picks a device from the open picker, so the swap has the whole human
  // pick-time as margin. The wireless-changed listener tracks the actual
  // session for the capped-toast fallback. Re-invoked on every playlist
  // load in case JW rebuilt the media element (the patch flag makes it
  // idempotent per element).
  const attachAirPlayListener = () => {
    const media = player.getContainer?.()?.querySelector('video')
    if (!media || !window.WebKitPlaybackTargetAvailabilityEvent) return
    if (media.swscaAirPlayIntentHook) return
    media.swscaAirPlayIntentHook = true
    const original = media.webkitShowPlaybackTargetPicker?.bind(media)
    if (original)
      media.webkitShowPlaybackTargetPicker = () => {
        setupSignedVariant('airplay-intent')
        original()
      }
    media.addEventListener(
      'webkitcurrentplaybacktargetiswirelesschanged',
      (event) =>
        setCastSession(
          Boolean(event.target?.webkitCurrentPlaybackTargetIsWireless)
        )
    )
    // Safari keeps the AirPlay route per tab: a media element created while a
    // session persists (chapter/tutorial navigation mid-cast) is born already
    // routed and never fires the changed event — observed on dev 2026-09-18
    // as a receiver fetching a cookie-only manifest with no swap at all. The
    // swap is post-route here, but the reload re-hands the receiver a signed
    // source, which is strictly better than stranding it.
    if (media.webkitCurrentPlaybackTargetIsWireless)
      setCastSession(true, 'airplay-route-resumed')
  }

  const checkExternalDialog = () => {
    const currentChapterIndex = player.getPlaylistIndex()
    const currentChapter = player.getPlaylistItem(currentChapterIndex)
    // Check if External User Funnel Dialog must be displayed
    const chapterLength = getSecondsFromTimestamp({
      durationH: currentChapter?.durationH,
      durationM: currentChapter?.durationM,
      durationS: currentChapter?.durationS
    })

    if (
      chapterLength > MEANINGFUL_CONTENT_LENGTH &&
      props.shouldExternalNewsletterDialogBeDisplayed
    ) {
      player.stop()
      props.openExternalUserFunnelDialog()
    }
  }
  /**
   *  Effects
   */
  // Effect to send tick every five seconds to server to track user progress
  useEffect(() => {
    if (
      !chapters.length ||
      !player ||
      !videoStatisticId ||
      currentChapterIndex === undefined ||
      !isPlaying ||
      disableStats
    )
      // If player is not ready, paused, or stats are disabled then disable interval
      return undefined
    // Get current chapter id
    const playlist = player.getPlaylist()
    const currentChapterId = playlist[currentChapterIndex]?.extra?.id
    // Get current caption selection
    const getCaptionsLabel = () => {
      const config = player.getConfig()

      return config?.captionsTrack?.label
    }
    // Create five seconds interval to send tick to server
    const intervalId = setInterval(() => {
      sendTick(
        videoStatisticId,
        currentChapterId,
        player.getPosition(),
        getCaptionsLabel()
      )
    }, TICK_FREQUENCY)

    // Clean up hook
    return () => {
      clearInterval(intervalId)
    }
  }, [chapters, player, videoStatisticId, currentChapterIndex, isPlaying])
  // Effect for resuming progress
  useEffect(() => {
    if (playerIsReady && player) {
      /**
       *  Resume progress logic
       *  Step 1 - Set active chapter
       */
      // Attempt to read chapter to play from URL
      const chapterViaQueryParam = getChapterFromURL(chapters.length)
      const playlist = player.getPlaylist()
      if (chapterViaQueryParam !== undefined) {
        const chapterId = playlist[chapterViaQueryParam]?.extra?.id
        setCurrentChapter(videoId, chapterViaQueryParam, chapterId)
      } else if (bookmarkLoaded) {
        const index = bookmarkLoaded.chapterOrder - 1
        const chapterId = playlist[index]?.extra?.id
        setCurrentChapter(videoId, index, chapterId)
      } else if (resume && playlist.length) {
        const index = resume.order - 1
        const chapterId = playlist[index]?.extra?.id
        setCurrentChapter(videoId, index, chapterId)
      }
      /**
       *  Resume progress logic
       *  Step 2 - Set active time and play chapter
       */
      if (chapterViaQueryParam !== undefined) {
        const chapter = playlist[chapterViaQueryParam]
        const chapterLength = getSecondsFromTimestamp({
          durationH: chapter?.durationH,
          durationM: chapter?.durationM,
          durationS: chapter?.durationS
        })
        const playlistItemId = chapter?.extra?.id
        const chapterProgress = userProgress[playlistItemId]
        // Look for time data in URL, or fall back to previous user progress if exists and chapter hasn't been completed
        const time =
          getChapterTimePositionFromURL(chapterLength) ||
          (chapterProgress?.completed &&
          chapterProgress?.progress > COMPLETION_THRESHOLD
            ? 0
            : chapterProgress?.currentPosition)
        playChapter(chapterViaQueryParam, time)
      }
      // If no chapter data is found in URL, attempt to auto-resume from stored data
      else if (resume) {
        const time = resume.time
        playChapter(resume.order - 1, time)
      }
      // If everything else fails, start from the beginning
      else playChapter(0, 0)
    }
  }, [playerIsReady, player])
  // Effect to load next tutorials on pathway
  useEffect(() => {
    if (playlistIsComplete) {
      const url = new URL(window.location.href)
      loadNextTutorials(tutorial.slug, url.searchParams.get('path_id'))
    }
  }, [currentChapterIndex, tutorial, playlistIsComplete])
  // Effect to display recommended tutorials at the end of the course
  useEffect(() => {
    if (recommends.length && !recommendedWereDisplayed && playlistIsComplete) {
      setDisplayModal(true)
      // Display recommended modal only once
      setRecommendedWereDisplayed(true)
    }
  }, [recommends, recommendedWereDisplayed, playlistIsComplete])
  // Effect to reload playlist and resume playback when toggling relevant chapters
  useEffect(() => {
    if (chapters && player) {
      // SPEC-057 FR-4: identical content = no reload, whatever minted the new
      // identity. This is the hard bound that holds even for churn sources
      // nobody predicted.
      const signature = playlistSignature(credentialedPlaylist)
      if (signature === lastLoadSignature.current) return
      lastLoadSignature.current = signature
      // AC-1 observability: count loads per current chapter key and raise a
      // Sentry breadcrumb past the threshold — measured, not asserted.
      const currentItem =
        player.getPlaylist()?.[player.getPlaylistIndex()] ||
        credentialedPlaylist[0]
      const chapterKey =
        currentItem?.sources?.[0]?.file?.split('&jwt=')[0] || 'unknown'
      chapterLoadCounts.current[chapterKey] =
        (chapterLoadCounts.current[chapterKey] || 0) + 1
      if (chapterLoadCounts.current[chapterKey] === CHAPTER_RELOAD_ALARM_THRESHOLD + 1)
        logger.error('Playlist reload exceeded per-chapter budget (SPEC-057)', {
          parameters: {
            chapterKey,
            count: chapterLoadCounts.current[chapterKey]
          }
        })
      const playStatus = player.getState()
      const restore = restoreOnReload.current
      restoreOnReload.current = false
      const index = player.getPlaylistIndex()
      const position = player.getPosition()
      // Reload the credentialed playlist, not the raw chapters — dropping
      // withCredentials here would send the next manifest/segment requests
      // cookieless and 403 on the private host (SPEC-010).
      player.load(credentialedPlaylist)

      // If player is playing, auto-play new playlist item after switching
      if (playStatus === 'playing')
        player.once('playlistItem', () => player.play())

      if (restore) {
        // Signed-variant swap (cast): same content, new URLs — put the user
        // back where they were. A seek issued before the new item has set up
        // is dropped by JW, so seek on the item's first `play` instead (the
        // same pattern playChapter uses). playlistItem() starts playback, so
        // a paused or idle player is paused again first.
        player.playlistItem(index)
        if (playStatus !== 'playing' && playStatus !== 'buffering')
          player.pause()
        if (position > 0)
          player.once('play', () => {
            if (player.getPlaylistIndex() === index) player.seek(position)
          })
      }
      // Remove chapter url if present to avoid inconsistencies
      else updateCurrentChapterURL()
    }
  }, [shouldOnlyShowRelevants, credentialedPlaylist])
  // SPEC-054 FR-7: countdown while rate-limited; when the window ends, clear
  // the notice and retry the playlist exactly once (never sooner).
  useEffect(() => {
    if (!rateLimit) return undefined
    const update = () =>
      setRateLimitMinutesLeft(
        Math.max(1, Math.ceil((rateLimit.resumesAtMs - Date.now()) / 60000))
      )
    update()
    const intervalId = setInterval(update, 30000)
    const timeoutId = setTimeout(() => {
      setRateLimit(null)
      rateLimitedUntil.current = 0
      recoveryAttempts.current = 0
      refreshPlaylist({
        signed: signedVariantActive.current && !signedVariantBlocked.current
      }).catch(() => {})
    }, Math.max(1000, rateLimit.resumesAtMs - Date.now()))
    return () => {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
    }
  }, [rateLimit])
  /**
   * Callbacks
   */
  const onReadyCallback = (status) => {
    if (status?.type !== 'ready') return
    // SPEC-057: JW was handed the initial playlist as a prop — record its
    // content signature so a later identity-only churn of the same content
    // cannot masquerade as the first "change" and trigger a load.
    lastLoadSignature.current = playlistSignature(credentialedPlaylist)

    // Stream-recovery and cast bindings apply to EVERY player, special
    // videos included. (They were previously inside the tutorial-only block,
    // which left SpecialVideoPlayer without recovery despite it passing
    // refreshPlaylist — SPEC-011 FR-4 gap, closed with SPEC-010 Phase 2.)
    player.on('error', errorHandler)
    player.on('setupError', errorHandler)
    player.on('time', ({ currentTime }) => {
      // A recovery has succeeded once playback is sustained past the point we
      // resumed from — that, not a resolved fetch, re-arms the budget.
      if (
        recoveryPosition.current !== null &&
        currentTime > recoveryPosition.current + SUSTAINED_PLAYBACK_SECONDS
      ) {
        recoveryAttempts.current = 0
        recoveryPosition.current = null
      }
      if (isSpecialVideo) return
      const currentlyPlaying = player.getPlaylistIndex()
      const playlist = player.getPlaylist()
      const chapterId = playlist[currentlyPlaying]?.extra?.id
      // Extract video duration from player config, this could be calculated once elsewhere, considering it doesn't change
      const config = player.getConfig()

      throttledChapterProgressUpdate(currentTime, chapterId, config?.duration)
    })
    player.on('cast', castHandler)
    attachAirPlayListener()
    player.on('playlist', attachAirPlayListener)

    if (isSpecialVideo) return

    // Tutorial-only bindings: statistics, chapter tracking, UI state
    const url = new URL(window.location.href)
    startTutorialStatistics(tutorial.id, url.searchParams.get('path_id'))

    player.on('playlistItem', playlistItemHandler)
    player.on('playlistComplete', playlistCompleteHandler)
    player.on('play', playHandler)
    player.on('pause', () => setIsPlaying(false))
    player.on('idle', () => setIsPlaying(false))
    player.on('complete', checkExternalDialog)
    player.once('viewable', (viewable) => setPlayerIsReady(!!viewable))
  }
  const didMountCallback = ({ player, id }) => {
    setPlayer(player)
    savePlayerId(id)
  }
  const willUnmountCallback = () => {
    if (recoveryTimeoutId.current) clearTimeout(recoveryTimeoutId.current)
    cleanPlayer()
  }
  /**
   * Component
   */
  return chapters.length > 0 ? (
    <div className={`video-player ${hideSidebar ? 'full' : ''}`}>
      {rateLimit && (
        <div className="player-rate-limit" role="alert">
          <p className="player-rate-limit__detail">{rateLimit.detail}</p>
          {!rateLimit.blocked && (
            <p className="player-rate-limit__countdown">
              Playback resumes in about {rateLimitMinutesLeft} min
            </p>
          )}
        </div>
      )}
      <div className="video-player__video">
        <JWPlayer
          library="../../global/vendor/jwplayer-8.48.1/jwplayer.js"
          playlist={credentialedPlaylist}
          didMountCallback={didMountCallback}
          willUnmountCallback={willUnmountCallback}
          onReady={onReadyCallback}
          config={{
            key: JWPLAYER_LICENSE_KEY,
            floating: {
              dismissible: true
            },
            ga: {
              label: 'mediaid',
              useUniversalAnalytics: false,
              sendEnhancedEvents: true
            },
            width: '100%',
            height: '100%',
            autostart: true,
            // SPEC-010: credential the player's media XHRs so the CloudFront
            // cookies are stored (manifest) and sent (segments) to the private
            // video/API hosts. See requiresCredentials above.
            withCredentials: requiresCredentials,
            playbackRateControls: true,
            playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
            cast: {},
            captions: {
              backgroundColor: '#000000',
              backgroundOpacity: 60,
              color: '#ffffff',
              edgeColor: 'black',
              edgeStyle: 'raised',
              fontFamily: 'Tahoma, sans-serif',
              fontOpacity: 100,
              fontSize: 14,
              windowColor: '000000',
              windowOpacity: 0
            }
          }}
        />
      </div>
      <PlayerSidebar
        player={player}
        playlist={chapters}
        isSpecialVideo={isSpecialVideo}
        chaptered={chaptered}
        playerIsReady={playerIsReady}
        createBookmark={createBookmark}
        toggleSidebar={toggleChaptersSidebar}
        playChapter={playChapter}
        currentChapter={currentChapterIndex}
        played={played}
        userBookmarks={userBookmarks}
        displayCreateBookmark={displayCreateBookmark}
        removeBookmark={removeBookmark}
        tutorial={tutorial}
        shouldOnlyShowRelevants={shouldOnlyShowRelevants}
        toggleShowingOnlyRelevantChapters={toggleShowingOnlyRelevantChapters}
        shouldShowRelevantToggle={relevantChapters?.length > 0}
        userProgress={userProgress}
        isPlaying={isPlaying}
        markChapterAsCompleted={markChapterAsCompleted}
        updateUserProgress={updateUserProgress}
      />
      <RelatedModal
        visible={displayModal}
        close={() => setDisplayModal(false)}
        recommends={recommends}
        isMobile={isMobile}
        learningPath={learningPath}
        isBasicSubscriber={isBasicSubscriber}
      />
    </div>
  ) : null
}

NewVideoPlayer.propTypes = {
  videoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  tutorial: PropTypes.shape(TutorialPropTypes),
  playlist: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      tracks: PropTypes.arrayOf(
        PropTypes.shape({
          // TODO: fill this
        })
      ),
      image: PropTypes.string,
      sources: PropTypes.arrayOf(
        PropTypes.shape({
          file: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired
        })
      )
    })
  ),
  videoStatisticId: PropTypes.number,
  apprenticeVideosSeen: PropTypes.arrayOf(
    PropTypes.shape(TutorialSummaryPropTypes)
  ).isRequired,
  isSubscriptionApprentice: PropTypes.bool.isRequired,
  played: PropTypes.arrayOf(PropTypes.number).isRequired,
  chapterParam: PropTypes.number,
  isMobile: PropTypes.bool,
  setCurrentChapter: PropTypes.func.isRequired,
  getPlayerState: PropTypes.func.isRequired,
  startTutorialStatistics: PropTypes.func.isRequired,
  sendTick: PropTypes.func.isRequired,
  clearStatistics: PropTypes.func.isRequired,
  addVideoSeen: PropTypes.func.isRequired,
  disableStats: PropTypes.bool,
  resume: PropTypes.shape({
    time: PropTypes.number,
    order: PropTypes.number
  }),
  isSpecialVideo: PropTypes.bool,
  onNoAccess: PropTypes.func
}

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { getPlayerState } = stateProps
  const { videoId } = ownProps
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    ...getPlayerState(videoId)
  }
}

export default connect(stateSelector, dispatchers, mergeProps)(NewVideoPlayer)
